// 메뉴 권한 관리 — 메뉴별 접근/관리 최소 역할 설정 (실장 이상 접근)
import { useEffect, useMemo, useState } from "react";
import { api } from "../../../shared/api/client";
import { Loading } from "../../../shared/components/Empty";
import { toast } from "../../../shared/stores/toasts";

type RoleOption = { code: string; name: string; rank: number };
type MenuPerm = {
  menu_key: string;
  label: string;
  section: string | null;
  min_access_role: string;
  min_manage_role: string;
  sort_order: number;
};
type ApiResp = { menus: MenuPerm[]; roles: RoleOption[] };

const SECTION_LABEL: Record<string, string> = {
  home: "홈", collab: "협업", work: "업무", ops: "운영", exec: "내부운영", etc: "기타",
};
const SECTION_ORDER = ["home", "collab", "work", "ops", "exec", "etc"];

export function AdminMenuPermissions() {
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [rows, setRows] = useState<MenuPerm[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get<ApiResp>("/iam/menu-permissions")
      .then((r) => { setRows(r.data.menus); setRoles(r.data.roles); setDirty(false); })
      .catch((e: any) => {
        toast.error("불러오기 실패", e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? "");
        setRows([]);
      });
  };
  useEffect(() => { load(); }, []);

  const setRole = (key: string, field: "min_access_role" | "min_manage_role", value: string) => {
    setRows((prev) => prev && prev.map((r) => r.menu_key === key ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  const save = async () => {
    if (!rows) return;
    setSaving(true);
    try {
      const r = await api.put<ApiResp>("/iam/menu-permissions", {
        items: rows.map((x) => ({
          menu_key: x.menu_key,
          min_access_role: x.min_access_role,
          min_manage_role: x.min_manage_role,
        })),
      });
      setRows(r.data.menus); setRoles(r.data.roles); setDirty(false);
      toast.success("저장됨", "메뉴 권한이 적용되었습니다. (다음 페이지 로드부터 반영)");
    } catch (e: any) {
      toast.error("저장 실패", e?.response?.data?.error?.message ?? e?.response?.data?.detail ?? "");
    } finally { setSaving(false); }
  };

  // Group rows by section
  const grouped = useMemo(() => {
    const g: Record<string, MenuPerm[]> = {};
    for (const r of rows ?? []) { (g[r.section ?? "etc"] ??= []).push(r); }
    return g;
  }, [rows]);

  if (rows === null) return <Loading/>;

  const RoleSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <select className="t-input" value={value} onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: 13, padding: "5px 8px", minWidth: 130 }}>
      {/* keep current value selectable even if not in role list */}
      {!roles.some((r) => r.code === value) && <option value={value}>{value}</option>}
      {roles.map((r) => (
        <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
      ))}
    </select>
  );

  return (
    <div className="t-grid cols-1" style={{ gap: 16 }}>
      {/* Header / save bar */}
      <div className="t-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>메뉴 접근 / 관리 권한</div>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 3, lineHeight: 1.6 }}>
            메뉴별로 <b>접근 가능</b>(메뉴를 볼 수 있는 최소 역할)과 <b>관리 가능</b>(메뉴 내용 수정·추가·삭제 가능한 최소 역할)을 지정합니다.
            선택한 역할 <b>이상</b>의 권한을 가진 사용자에게 적용됩니다.
          </div>
        </div>
        <button className="t-btn t-btn-primary" onClick={save} disabled={!dirty || saving}
          style={{ fontSize: 13, flexShrink: 0 }}>
          {saving ? "저장 중…" : dirty ? "변경사항 저장" : "저장됨"}
        </button>
      </div>

      {SECTION_ORDER.filter((s) => grouped[s]?.length).map((section) => (
        <div key={section} className="t-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{
            padding: "10px 18px", borderBottom: "1px solid var(--border)",
            background: "rgba(0,0,0,0.02)", fontWeight: 600, fontSize: 13,
          }}>
            {SECTION_LABEL[section] ?? section}
          </div>
          {/* Column header */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 160px 160px",
            padding: "8px 18px", borderBottom: "1px solid var(--border)",
            fontSize: 11, fontWeight: 600, color: "var(--text-3)",
            textTransform: "uppercase", letterSpacing: "0.04em", gap: 12,
          }}>
            <span>메뉴</span><span>접근 가능 (이상)</span><span>관리 가능 (이상)</span>
          </div>
          {grouped[section].map((r) => (
            <div key={r.menu_key} style={{
              display: "grid", gridTemplateColumns: "1fr 160px 160px",
              padding: "8px 18px", borderBottom: "1px solid var(--border)",
              alignItems: "center", gap: 12,
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{r.label}</div>
                <div className="mono" style={{ fontSize: 10.5, color: "var(--text-3)" }}>{r.menu_key}</div>
              </div>
              <RoleSelect value={r.min_access_role} onChange={(v) => setRole(r.menu_key, "min_access_role", v)} />
              <RoleSelect value={r.min_manage_role} onChange={(v) => setRole(r.menu_key, "min_manage_role", v)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
